import { Link } from '@tanstack/react-router';
import { Wordmark } from '../components/Wordmark';
import { Button } from '../components/Button';
import { Panel } from '../components/Card';
import { useAuthStore } from '../lib/auth';

/**
 * Landing route.
 *
 * Layout: two columns on wide screens (left = headline + CTAs, right = a
 * decorative "draft" of three example cards). Single column on narrow.
 * Everything fits in one viewport — internal panels never scroll vertically.
 */
export function LandingRoute() {
  const user = useAuthStore((s) => s.user);
  if (user) return <PlayerHome displayName={user.displayName} isSupporter={user.isSupporter} />;

  return (
    <div className="h-full w-full grid place-items-center px-5 md:px-10">
      <div className="w-full max-w-[1180px] grid md:grid-cols-[1.1fr_1fr] gap-10 md:gap-14 items-center">
        {/* Left: headline */}
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent font-semibold">
            <Pip />
            <span>Chess. With a twist.</span>
          </div>
          <h1 className="mt-5">
            <Wordmark size="lg" wrap />
          </h1>
          <p className="mt-6 text-lg md:text-xl text-ink-muted leading-snug max-w-[520px]">
            Every match starts with a card draft. Pick one of three rule modifiers, then play
            chess with it on for the whole game. Capture the king to win &mdash; <em>check is gone</em>.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/join">
              <Button size="lg">Join now</Button>
            </Link>
            <Link to="/play/bot">
              <Button size="lg" variant="secondary">
                Play a bot
              </Button>
            </Link>
            <Link to="/help">
              <Button size="lg" variant="ghost">
                Guide
              </Button>
            </Link>
          </div>
          <div className="mt-10 grid grid-cols-3 gap-6 max-w-md text-sm">
            <Stat number="27" label="rule cards" />
            <Stat number="351" label="card matchups" />
            <Stat number="∞" label="ways to lose" />
          </div>
        </div>

        {/* Right: a small "draft" tableau — three cards fanned out. Decorative. */}
        <div className="relative h-[360px] md:h-[460px] hidden md:block">
          <DemoCard
            title="Relentless March"
            category="Pawn"
            blurb="Your pawns may advance 1 or 2 squares at any time. First move may be 3."
            tilt={-9}
            offset={{ x: -110, y: 12 }}
            rarity="common"
          />
          <DemoCard
            title="Telekinetic Strike"
            category="King"
            blurb="The king captures an adjacent orthogonal piece without moving."
            tilt={0}
            offset={{ x: 0, y: -24 }}
            rarity="legendary"
            featured
          />
          <DemoCard
            title="Vaulting Bishop"
            category="Bishop"
            blurb="Bishops jump over exactly one piece per diagonal move."
            tilt={9}
            offset={{ x: 110, y: 16 }}
            rarity="rare"
          />
        </div>
      </div>
    </div>
  );
}

function PlayerHome({ displayName, isSupporter }: { displayName: string; isSupporter: boolean }) {
  return (
    <div className="h-full w-full max-w-[1180px] mx-auto px-5 md:px-10 py-6 grid lg:grid-cols-[1fr_420px] gap-5 overflow-hidden">
      <section className="min-h-0 flex flex-col justify-center">
        <div className="text-xs uppercase tracking-[0.18em] text-accent font-semibold">
          Ready when you are
        </div>
        <h1 className="mt-3 font-display text-[clamp(36px,6vw,68px)] font-black leading-none">
          Welcome, {displayName}
        </h1>
        <p className="mt-5 max-w-[560px] text-lg text-ink-muted leading-snug">
          Draft a rule, start a clock, and get to the board. Multiplayer stays behind login;
          bot games are always available.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/play/bot">
            <Button size="lg">Play a bot</Button>
          </Link>
          <Link to="/play">
            <Button size="lg" variant="secondary">
              Find a game
            </Button>
          </Link>
          <Link to="/settings">
            <Button size="lg" variant="ghost">
              Appearance
            </Button>
          </Link>
        </div>
      </section>
      <aside className="min-h-0 grid content-center gap-4">
        <Panel className="p-5">
          <div className="text-xs uppercase tracking-wider text-ink-muted">Account</div>
          <div className="mt-2 font-display text-2xl font-bold">
            {isSupporter ? 'Supporter' : 'Member'}
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {isSupporter
              ? 'Supporter themes are unlocked on this account.'
              : 'Support the site once to unlock Supporter Themes.'}
          </p>
        </Panel>
        <Panel className="p-5">
          <div className="text-xs uppercase tracking-wider text-ink-muted">Fast start</div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <Link to="/play/bot" className="rounded-md border border-ink/10 p-3 hover:bg-ink/5 focus-ring">
              5 + 3 bot
            </Link>
            <Link to="/cards" className="rounded-md border border-ink/10 p-3 hover:bg-ink/5 focus-ring">
              Card list
            </Link>
            <Link to="/support" className="rounded-md border border-ink/10 p-3 hover:bg-ink/5 focus-ring">
              Report bug
            </Link>
            <Link to="/help" className="rounded-md border border-ink/10 p-3 hover:bg-ink/5 focus-ring">
              Guide
            </Link>
          </div>
        </Panel>
      </aside>
    </div>
  );
}

function Pip() {
  return <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent" aria-hidden />;
}

function Stat({ number, label }: { number: string; label: string }) {
  return (
    <div>
      <div className="font-display text-3xl font-bold leading-none">{number}</div>
      <div className="mt-1 text-xs uppercase tracking-wider text-ink-muted">{label}</div>
    </div>
  );
}

function DemoCard({
  title,
  category,
  blurb,
  tilt,
  offset,
  rarity,
  featured,
}: {
  title: string;
  category: string;
  blurb: string;
  tilt: number;
  offset: { x: number; y: number };
  rarity: 'common' | 'rare' | 'legendary';
  featured?: boolean;
}) {
  const rimVar = `--c-card-rim-${rarity}`;
  return (
    <div
      className={`absolute left-1/2 top-1/2 w-[240px] h-[340px] rounded-card overflow-hidden bg-surface shadow-card border ${
        featured ? 'z-10 ring-2 ring-accent/40' : 'z-0'
      }`}
      style={{
        transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) rotate(${tilt}deg)`,
        borderColor: `rgb(var(${rimVar}) / 0.55)`,
        boxShadow:
          featured
            ? '0 12px 28px rgb(26 20 16 / 0.18), 0 2px 0 rgb(26 20 16 / 0.04)'
            : '0 8px 22px rgb(26 20 16 / 0.10)',
      }}
    >
      <div
        className="h-2 w-full"
        style={{ background: `rgb(var(${rimVar}))` }}
        aria-hidden
      />
      <div className="p-4 h-full flex flex-col">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
          {category}
        </div>
        <h3 className="mt-1 font-display text-2xl font-bold leading-tight">{title}</h3>
        <div className="mt-3 h-[1px] w-12 bg-ink/15" />
        <p className="mt-3 text-sm text-ink-muted leading-snug flex-1">{blurb}</p>
        <div className="mt-2 text-[10px] uppercase tracking-wide font-semibold text-ink-muted">
          {rarity}
        </div>
      </div>
    </div>
  );
}
